import { call, SagaGenerator, select, takeLatest } from "typed-redux-saga";

import { Annotation } from "@com.mgmtp.a12.base/base-model-api/lib/main/header";
import { Activity, ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { FormActivity } from "@com.mgmtp.a12.formengine/formengine-core/lib/client-extensions";
import { LoggerFactory } from "@com.mgmtp.a12.utils/utils-logging";
import { JsonRpc2Response } from "@com.mgmtp.a12.dataservices/dataservices-access";

import { localizableFromLocaleResources, RESOURCE_KEYS } from "../../../localization";
import { reloadActivity, saveActivity } from "../../../utilities/activityUtils";
import { putErrorNotification } from "../../../utilities/notificationUtils";
import { isJsonRpc2ResponseArray, signDocument } from "../../../utilities/rpcHelper";
import { screenValidation } from "../../../utilities/validationUtils";
import {
	PATH_TO_FILE_ATTACHMENT_LOCATION,
	SIGNATUR_EINBETTEN,
	SIGNATUR_POSITION
} from "../../../utilities/variables/modulfRuntimeAnnotations";
import { executeBusy } from "../../../utilities/workflowUtils";
import { SIGN_PDF_EVENT } from "../../../utilities/variables/customEventNames";
import { extractErrorMessage, isRpcErrorResponse } from "../../../utilities/error";
import { normalizePlatzhalterA12Path } from "../../../utilities/platzhalterHelper";
import { ButtonAction, createEventButtonActionMatcher } from "../../../utilities/actionUtils";
import { StammdatenFormDescriptor } from "../../../utilities/descriptors";
import { ModulfUiSelectors } from "../../../utilities/selectors/modulfUiSelectors";

/** Error-Key aus {@code SignUserNotReadyException.ERROR_KEY} im Backend. */
const SIGN_USER_NOT_READY_KEY = "sign.user.not.ready";

import SingleDocumentData = FormActivity.Data.SingleDocumentData;

const logger = LoggerFactory.getLogger("modulf");

/**
 * Reagiert auf den Buttonklick zum PDF-Signieren und erstellt einen Signier-Request.
 */
export function* signPdfSaga(): SagaGenerator<void> {
	yield* takeLatest(createEventButtonActionMatcher(SIGN_PDF_EVENT), handle);
}

function* handle(action: ButtonAction): SagaGenerator<void> {
	const activityId = action.payload.activityId;
	const activity = yield* select(ActivitySelectors.activityById(activityId));

	// Stammdaten haben kein Prozess-/Screen-Konzept, gegen das eine vollumfängliche
	// Validierung sinnvoll ausgeführt werden könnte – dort wird ungeprüft signiert.
	const stammdatenForm = StammdatenFormDescriptor.isInstance(activity?.descriptor);

	yield* call(executeBusy, function* (): SagaGenerator<void> {
		if (!stammdatenForm) {
			const isValid = yield* call(screenValidation, activityId);
			if (!isValid) {
				logger.error("Document is not valid.");
				return;
			}
		}

		yield* call(signAndUpdateDocument(action));
	});
}

function signAndUpdateDocument(action: ButtonAction): () => SagaGenerator<void> {
	return function* (): SagaGenerator<void> {
		const activityId = action.payload.activityId;
		const activity = yield* select(ActivitySelectors.activityById(activityId));
		if (!activity) {
			throw new Error("Couldn't find activity");
		}

		const group = `signPdf:${activity.descriptor.instance}`;

		const isSaved = yield* saveActivity(activityId);
		if (!isSaved) {
			yield* call(handleError, "Could not save activity " + activityId, group);
			return;
		}

		const data = yield* select(ActivitySelectors.data(activityId));
		if (!SingleDocumentData.isInstance(data)) {
			yield* call(handleError, "Could not find document data in " + activity.id, group);
			return;
		}

		// Button-Annotations lesen (enthält Pfad, Signaturposition und einbetten-Flag)
		const annotations = yield* select(
			ModulfUiSelectors.selectButtonAnnotations(activityId, action.payload.engineEvent.payload.buttonPath)
		);

		try {
			yield* call(signPdf, data.document, annotations);
			// pageVisitUrl ist jetzt im Dokument gespeichert und wird nach dem Reload
			// über das FormModel als anklickbarer Link ("Klicken Sie hier, um zu Signieren") angezeigt.
			yield* call(reloadActivity, activity);
		} catch (e) {
			yield* call(handleError, e, group);
		}
	};
}

/**
 * Sendet einen Request an den SignService, um ein PDF zu signieren.
 * Gibt die pageVisitUrl zurück (2FA-Link für sign-me), oder null wenn kein Redirect nötig.
 */
export async function signPdf(
	document: Activity.Data.Document,
	annotations: readonly Annotation[]
): Promise<string | null> {
	let pathToFileAttachments = annotations?.find(
		(a: Annotation) => a.name === PATH_TO_FILE_ATTACHMENT_LOCATION
	)?.value;
	// Attachment-Pfad enthält vor root noch den Modellnamen – muss entfernt werden
	pathToFileAttachments = normalizePlatzhalterA12Path(pathToFileAttachments);
	if (!pathToFileAttachments) {
		return Promise.reject(new Error("No pathToFileAttachmentLocation annotation given!"));
	}

	const signaturPosition = annotations.find((a: Annotation) => a.name === SIGNATUR_POSITION)?.value;
	const signaturEinbettenValue = annotations.find((a: Annotation) => a.name === SIGNATUR_EINBETTEN)?.value;
	const signaturEinbetten = signaturEinbettenValue === "true";

	return signDocument(document.id, pathToFileAttachments, signaturEinbetten, signaturPosition);
}

export function* handleError(error: any, group: string): SagaGenerator<void> {
	logger.error("Fehler beim Signieren des Dokuments", error);

	let errorMessage = localizableFromLocaleResources(RESOURCE_KEYS.error.GENERATE_DOCUMENT_ERROR);

	if (isJsonRpc2ResponseArray(error)) {
		const rpcError = error.filter(JsonRpc2Response.error.isInstance).at(0)?.error;

		// Dedizierter Fehlerfall: Sachbearbeiter bei sign-me nicht signierbereit
		if (isRpcErrorResponse(rpcError) && rpcError.data.description.key === SIGN_USER_NOT_READY_KEY) {
			errorMessage = localizableFromLocaleResources(RESOURCE_KEYS.error.SIGN_USER_NOT_READY_ERROR, {
				cause: { type: "plain", value: rpcError.data.description.default }
			});
		} else {
			const rootCause = error
				.filter(JsonRpc2Response.error.isInstance)
				.map(it => extractErrorMessage(it.error))
				?.find(it => it !== null);
			if (rootCause !== null) {
				errorMessage = localizableFromLocaleResources(RESOURCE_KEYS.error.SIGN_DOCUMENT_WITH_CAUSE_ERROR, {
					cause: { type: "plain", value: rootCause }
				});
			}
		}
	}

	yield* call(putErrorNotification, errorMessage, error, undefined, group);
}
